import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import './BanIpConfirm.css';

const BanIpConfirm = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const ip = useMemo(() => searchParams.get('ip') || '', [searchParams]);
  const password = useMemo(() => searchParams.get('password') || '', [searchParams]);

  useEffect(() => {
    const controller = new AbortController();

    async function loadPreview() {
      if (!ip || !password) {
        setError('This ban link is missing required information.');
        setLoading(false);
        return;
      }

      try {
        setError('');
        const query = new URLSearchParams({ ip, password });
        const response = await fetch(`/api/v1/ban-ip/preview?${query.toString()}`, {
          signal: controller.signal,
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.detail || 'This ban link is invalid.');
        }

        const data = await response.json();
        setPreview(data);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError(err.message || 'This ban link is invalid.');
        }
      } finally {
        setLoading(false);
      }
    }

    loadPreview();
    return () => controller.abort();
  }, [ip, password]);

  const handleConfirm = async () => {
    setConfirming(true);
    setError('');

    try {
      const response = await fetch('/api/v1/ban-ip/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ip, password }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Could not complete the ban.');
      }

      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError(err.message || 'Could not complete the ban.');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Confirm IP Ban | NowTarot AI</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <main className="ban-confirm-page">
        <div className="ban-confirm-card">
          <p className="ban-confirm-kicker">Moderation Action</p>
          <h1>Confirm IP ban</h1>

          {loading ? <p>Checking ban link…</p> : null}

          {!loading && error ? (
            <div className="ban-confirm-message ban-confirm-error">{error}</div>
          ) : null}

          {!loading && !error && !result && preview ? (
            <>
              <p className="ban-confirm-copy">
                Are you sure you want to permanently ban <strong>{preview.ip}</strong>?
              </p>
              <p className="ban-confirm-note">
                Once confirmed, this IP will be blocked site-wide and shown a page stating they are
                banned and not welcome to use NowTarot. That page also stamps a durable browser ban
                token so a later IP change can auto-reban them.
              </p>
              {preview.already_blocked ? (
                <div className="ban-confirm-message ban-confirm-warning">
                  This IP is already blocked. Confirming again will refresh the site-level block.
                </div>
              ) : null}
              <button
                type="button"
                className="ban-confirm-button"
                onClick={handleConfirm}
                disabled={confirming}
              >
                {confirming ? 'Banning…' : 'Confirm permanent ban'}
              </button>
            </>
          ) : null}

          {!loading && !error && result ? (
            <div className="ban-confirm-message ban-confirm-success">
              <strong>{result.ip}</strong> is now permanently banned.
            </div>
          ) : null}
        </div>
      </main>
    </>
  );
};

export default BanIpConfirm;
